<script>
  import { onMount, onDestroy, tick } from 'svelte';
  import { listAdminAgreements, uploadAdminAgreement, searchAgreementStudents,
    assignAgreementStudents, previewAdminAgreement, agreementStatusLabel, withdrawAgreementAssignment,
    getAllAgreementAssignments, getAllAgreementStudents } from './adminAgreementApi.js';

  let rows = [], page = 1, hasNext = false, filter = '';
  let loading = true, busy = false, allowed = false, error = '', message = '';
  let stage = 'library', active = null, title = '', file = null, uploadId = null, uploadAttempted = false;
  let selected = [], query = '', candidates = [], searchPage = 1, searchNext = false, searching = false, confirmed = false;
  let detailsId = null, details = [], detailsLoading = false, detailsFailed = false, detailsAbort;
  const assignmentStatuses = ['assigned', 'signing_in_progress', 'signed_storage_pending', 'finalization_failed', 'completed_stored', 'withdrawn'];
  $: detailGroups = groupAssignments(details);

  function groupAssignments(entries) {
    const groups = new Map(assignmentStatuses.map(status => [status, []]));
    for (const entry of entries) {
      if (!groups.has(entry.status)) groups.set(entry.status, []);
      groups.get(entry.status).push(entry);
    }
    return [...groups].filter(([, entries]) => entries.length).map(([status, entries]) => ({ status, entries }));
  }
  let previewId = null, heading, searchTimer, searchAbort, searchGeneration = 0, loadGeneration = 0, detailGeneration = 0;
  let alive = true;
  let recipientFilter = 'students', removingId = null;
  let startDate = '', endDate = '', appliedDates = null;
  $: dateRangeReady = recipientFilter !== 'active_students_by_date' || appliedDates !== null;
  let existingIds = new Set(), preloading = false, selectionReady = false, selectingAll = false;
  let selectionGeneration = 0, selectionAbort;
  $: newSelected = selected.filter(student => !existingIds.has(student.id));
  const recipientFilters = [['all', 'Visi naudotojai'], ['students', 'Mokiniai'], ['teachers', 'Mokytojai'], ['active_students', 'Aktyvūs mokiniai']];

  function report(exc) {
    error = exc?.message || 'Ryšys nutrūko. Atnaujinkite sąrašą arba bandykite dar kartą.';
    if ([401, 403].includes(exc?.status)) { allowed = false; rows = []; selected = []; details = []; }
  }

  async function load(nextPage = page) {
    const generation = ++loadGeneration;
    loading = true;
    try {
      const result = await listAdminAgreements(filter, nextPage);
      if (!alive || generation !== loadGeneration) return;
      rows = result.results; page = result.page; hasNext = result.has_next; allowed = true;
    } catch (exc) { if (alive && generation === loadGeneration) report(exc); }
    finally { if (alive && generation === loadGeneration) loading = false; }
  }

  function stopSearch() {
    clearTimeout(searchTimer); searchAbort?.abort(); searchGeneration++; searching = false;
  }

  async function focusHeading() { await tick(); heading?.focus(); }

  function beginUpload() {
    stopSearch(); stopSelection(); stage = 'upload';
    title = ''; file = null; uploadId = crypto.randomUUID(); uploadAttempted = false;
    error = ''; message = ''; active = null; focusHeading();
  }

  function stopSelection() {
    selectionGeneration++; selectionAbort?.abort(); preloading = false; selectingAll = false;
  }

  async function chooseStudents(row) {
    stopSearch(); stopSelection(); active = row; stage = 'students'; selected = []; existingIds = new Set(); query = ''; candidates = [];
    searchNext = false; confirmed = false; error = ''; message = ''; focusHeading();
    recipientFilter = 'students'; startDate = ''; endDate = ''; appliedDates = null; search(1);
    preloading = true; selectionReady = false; selectionAbort = new AbortController();
    const generation = selectionGeneration;
    try {
      const entries = await getAllAgreementAssignments(row.public_id, selectionAbort.signal);
      if (!alive || generation !== selectionGeneration) return;
      selected = [...new Map(entries.filter(entry => entry.status !== 'withdrawn')
        .flatMap(entry => (entry.members?.length ? entry.members : [entry.student]).map(student => [student.id, { ...student, assignmentStatus: entry.status }]))).values()];
      existingIds = new Set(selected.map(student => student.id)); selectionReady = true;
    } catch (exc) { if (alive && generation === selectionGeneration && exc.name !== 'AbortError') report(exc); }
    finally { if (alive && generation === selectionGeneration) preloading = false; }
  }

  async function back() {
    if (busy) return;
    stopSearch(); stopSelection(); stage = 'library'; active = null; error = ''; confirmed = false;
    await load(1); focusHeading();
  }

  async function upload() {
    if (busy || !file || !title.trim()) return;
    if (file.type !== 'application/pdf' || file.size > 20 * 1024 * 1024) {
      error = 'Pasirinkite PDF dokumentą iki 20 MiB.'; return;
    }
    busy = true; error = ''; uploadAttempted = true;
    const form = new FormData();
    form.append('upload_id', uploadId); form.append('title', title.trim()); form.append('source_pdf', file);
    try { const row = await uploadAdminAgreement(form); if (alive) chooseStudents(row); }
    catch (exc) {
      if (alive) {
        report(exc);
        if (!exc.status || exc.status >= 500) error = 'Įkėlimo rezultatas nepatvirtintas. Kartokite tą patį įkėlimą arba grįžkite į sąrašą — juodraštis galėjo būti išsaugotas.';
        else if (exc.status === 400) { uploadAttempted = false; }
      }
    } finally { busy = false; }
  }

  function scheduleSearch() {
    stopSearch(); candidates = []; searchNext = false;
    if (hasDateRange() && (!query.trim() || query.trim().length >= 2)) searchTimer = setTimeout(() => search(1), 300);
  }

  function setRecipientFilter(value) {
    if (value === recipientFilter) return;
    if (value === 'active_students_by_date' || recipientFilter === 'active_students_by_date') invalidateDateRange();
    recipientFilter = value; scheduleSearch();
  }

  function hasDateRange() {
    return recipientFilter !== 'active_students_by_date' || appliedDates !== null;
  }

  function invalidateDateRange() {
    stopSearch(); stopSelection(); appliedDates = null;
    candidates = []; searchNext = false; confirmed = false; error = '';
    selected = selected.filter(student => existingIds.has(student.id));
  }

  function applyDateRange() {
    invalidateDateRange();
    if (!startDate || !endDate || startDate > endDate) {
      error = 'Pasirinkite pradžios ir pabaigos datą. Pradžios data turi būti ne vėlesnė už pabaigos datą.';
      return;
    }
    appliedDates = { start_date: startDate, end_date: endDate };
    search(1);
  }

  async function search(nextPage) {
    if (!hasDateRange()) return;
    searchAbort?.abort(); searchAbort = new AbortController();
    const generation = ++searchGeneration, id = active.public_id;
    searching = true;
    try {
      const result = await searchAgreementStudents(id, query, nextPage, searchAbort.signal, recipientFilter,
        recipientFilter === 'active_students_by_date' ? { ...appliedDates } : null);
      if (!alive || generation !== searchGeneration) return;
      candidates = result.results; searchPage = result.page; searchNext = result.has_next;
    } catch (exc) { if (alive && generation === searchGeneration && exc.name !== 'AbortError') report(exc); }
    finally { if (alive && generation === searchGeneration) searching = false; }
  }

  function toggleStudent(student) {
    if (student.can_assign === false || !selectionReady || selectingAll) return;
    if (selected.some(row => row.id === student.id)) selected = selected.filter(row => row.id !== student.id);
    else if (existingIds.has(student.id) || newSelected.length < 1000) selected = [...selected, student];
    confirmed = false;
  }

  function clearSelection() {
    selected = []; confirmed = false;
  }

  async function selectAllFiltered() {
    if (!selectionReady || selectingAll || !hasDateRange() || (query.trim() && query.trim().length < 2)) return;
    selectingAll = true; confirmed = false; error = ''; selectionAbort = new AbortController();
    const generation = ++selectionGeneration;
    try {
      const users = await getAllAgreementStudents(active.public_id, query, recipientFilter, selectionAbort.signal,
        recipientFilter === 'active_students_by_date' ? { ...appliedDates } : null);
      if (!alive || generation !== selectionGeneration) return;
      const merged = [...new Map([...selected, ...users.filter(user => user.can_assign === true)]
        .map(student => [student.id, student])).values()];
      if (merged.filter(student => !existingIds.has(student.id)).length > 1000) {
        throw new Error('Vienu kartu galima priskirti iki 1000 naujų naudotojų. Susiaurinkite filtrą arba paiešką. Pasirinkimas nepakeistas.');
      }
      selected = merged;
    } catch (exc) { if (alive && generation === selectionGeneration && exc.name !== 'AbortError') report(exc); }
    finally { if (alive && generation === selectionGeneration) selectingAll = false; }
  }

  function closeDetails() {
    detailGeneration++; detailsAbort?.abort(); detailsId = null; details = []; detailsLoading = false; detailsFailed = false;
  }

  async function showDetails(id) {
    detailsAbort?.abort(); detailsAbort = new AbortController();
    const generation = ++detailGeneration;
    detailsId = id; detailsLoading = true; details = []; detailsFailed = false; error = '';
    try {
      const entries = await getAllAgreementAssignments(id, detailsAbort.signal);
      if (!alive || generation !== detailGeneration) return;
      details = entries;
    } catch (exc) { if (alive && generation === detailGeneration && exc.name !== 'AbortError') { detailsFailed = true; report(exc); } }
    finally { if (alive && generation === detailGeneration) detailsLoading = false; }
  }

  async function assign() {
    if (busy || !selectionReady || selectingAll || !hasDateRange() || !confirmed || !newSelected.length) return;
    busy = true; error = ''; stopSearch();
    const id = active.public_id;
    try {
      const result = await assignAgreementStudents(id, newSelected.map(row => row.id));
      if (!alive) return;
      stage = 'library'; message = `Sukurta priskyrimų: ${result.created_count}. Kiti priskyrimai nepakeisti.`;
      await load(1); await showDetails(id); focusHeading();
    } catch (exc) {
      if (alive) { report(exc); confirmed = false; await load(); }
    } finally { busy = false; }
  }

  async function removeAssignment(versionId, entry) {
    if (removingId || !entry.can_withdraw) return;
    const target = entry.family ? `visai šeimai „${entry.family.name}“` : `naudotojui „${entry.student.username}“`;
    if (!window.confirm(`Pašalinti dokumento priskyrimą ${target}? Dokumentas nebebus rodomas šiose paskyrose. Priskyrimo istorija bus išsaugota.`)) return;
    removingId = entry.public_id; error = '';
    try {
      await withdrawAgreementAssignment(versionId, entry.public_id);
      if (!alive) return;
      message = 'Priskyrimas pašalintas. Istorija išsaugota.';
      await load();
      if (detailsId === versionId) await showDetails(versionId);
    } catch (exc) {
      if (alive) { report(exc); if (exc.status === 409 && detailsId === versionId) await showDetails(versionId); }
    } finally { removingId = null; }
  }

  async function preview(id) {
    if (previewId) return;
    previewId = id; error = '';
    try { await previewAdminAgreement(id); } catch (exc) { report(exc); }
    finally { previewId = null; }
  }

  onMount(() => load(1));
  onDestroy(() => { alive = false; stopSearch(); stopSelection(); loadGeneration++; closeDetails(); });
</script>

<section class="agreements" aria-label="Dokumentų administravimas" aria-busy={busy}>
  <h2 bind:this={heading} tabindex="-1">Dokumentai pasirašymui</h2>
  {#if error}<p role="alert" class="error">{error}</p>{/if}
  {#if message}<p role="status" class="success">{message}</p>{/if}
  {#if loading}<p role="status">Įkeliamas dokumentų sąrašas…</p>{/if}
  {#if !allowed && !loading}<button on:click={() => { error = ''; load(1); }}>Bandyti dar kartą</button>{/if}
  {#if allowed}
    {#if stage === 'library'}
      <div class="toolbar">
        <button class="primary" on:click={() => beginUpload()}>Įkelti dokumentą</button>
        <button disabled={loading} on:click={() => { error = ''; load(); }}>Atnaujinti sąrašą</button>
      </div>
      <form class="toolbar" on:submit|preventDefault={() => load(1)}>
        <label>Ieškoti dokumentų<input bind:value={filter} maxlength="240" /></label>
        <button disabled={loading}>Ieškoti</button>
      </form>
      {#if !loading && !rows.length}<p>Dokumentų nėra. Įkelkite pirmą dokumentą.</p>{/if}
      <div class="library">
        {#each rows as row (row.public_id)}
          <article>
            <h3>{row.title}</h3>
            <p>{row.document_title} · Versija {row.version_number} · {agreementStatusLabel(row.status)}{!row.is_enabled ? ' · Išjungtas' : ''}</p>
            <p>Priskirta: {row.assigned_count} · Pasirašyta: {row.signed_count}</p>
            <div class="toolbar">
              <button disabled={!!previewId} on:click={() => preview(row.public_id)}>{previewId === row.public_id ? 'Atidaromas dokumentas…' : 'Peržiūrėti PDF'}</button>
              {#if row.can_assign}<button class="primary" on:click={() => chooseStudents(row)}>Priskirti mokiniams</button>{/if}
              <button aria-expanded={detailsId === row.public_id} on:click={() => detailsId === row.public_id ? closeDetails() : showDetails(row.public_id)}>Priskyrimai ir būsena</button>
            </div>
            {#if detailsId === row.public_id}
              <section class="details selection-panel" aria-label="Priskyrimai ir būsena">
                <div class="panel-heading"><div><span class="section-eyebrow">Dokumento priskyrimai</span><h4>Priskyrimai ir būsena</h4><p>Peržiūrėkite pasirašymo eigą ir tvarkykite nepasirašytus priskyrimus.</p></div></div>
                <div class="assignment-toolbar"><button disabled={detailsLoading} on:click={() => showDetails(row.public_id)}>Atnaujinti būsenas</button>{#if !detailsLoading && !detailsFailed}<span class="selected-count">Iš viso: {details.length}</span>{/if}</div>
                {#if detailsLoading}<p role="status">Įkeliami priskyrimai…</p>
                {:else if detailsFailed}<p class="hint">Priskyrimų įkelti nepavyko. Atnaujinkite būsenas ir bandykite dar kartą.</p>
                {:else}
                  {#if !details.length}<p>Mokinių dar nepriskirta.</p>{/if}
                  <div class="assignment-groups">
                    {#each detailGroups as group (group.status)}
                      <section class="assignment-group" class:complete={group.status === 'completed_stored'} class:review={group.status === 'finalization_failed'} class:waiting={group.status === 'assigned'} aria-labelledby={`assignment-${row.public_id}-${group.status}`}>
                        <h5 id={`assignment-${row.public_id}-${group.status}`}><span>{agreementStatusLabel(group.status)}</span><span class="status-count">{group.entries.length}</span></h5>
                        <ul class="assignment-list" aria-label={agreementStatusLabel(group.status)}>{#each group.entries as entry (entry.public_id)}<li>
                          <div class="student-info"><strong>{entry.family ? entry.family.name : `${entry.student.first_name} ${entry.student.last_name}`}</strong>{#if entry.family}<span class="student-meta">{entry.members.map(member => `${member.first_name} ${member.last_name}`).join(", ")}</span>{/if}<span class="student-meta">{entry.student.username}</span></div>
                          {#if entry.can_withdraw}<button class="remove-student" disabled={!!removingId} aria-label={`Pašalinti priskyrimą ${entry.student.username}`} on:click={() => removeAssignment(row.public_id, entry)}>{removingId === entry.public_id ? 'Šalinama…' : 'Pašalinti priskyrimą'}</button>{/if}
                        </li>{/each}</ul>
                      </section>
                    {/each}
                  </div>
                  <p class="hint">Pasirašyti dokumentai nešalinami. Vykstant pasirašymui ar tikrinant jo rezultatą, priskyrimo pašalinimas negalimas. Atšaukti priskyrimai lieka istorijoje.</p>
                {/if}
              </section>
            {/if}
          </article>
        {/each}
      </div>
      <div class="toolbar"><button disabled={page <= 1 || loading} on:click={() => load(page - 1)}>Ankstesni dokumentai</button><span>Puslapis {page}</span><button disabled={!hasNext || loading} on:click={() => load(page + 1)}>Kiti dokumentai</button></div>
    {:else if stage === 'upload'}
      <h3>1. Dokumentas</h3>
      <p>Įkelkite PDF kaip atskirą dokumentą ir pasirinkite, kam jį priskirti. Ankstesni dokumentai, priskyrimai ir parašai nebus pakeisti.</p>
      <form on:submit|preventDefault={upload}>
        <fieldset disabled={busy}>
          <label>Dokumento pavadinimas<input bind:value={title} maxlength="240" required disabled={uploadAttempted} /></label>
          <label>PDF dokumentas<input type="file" accept="application/pdf,.pdf" required disabled={uploadAttempted} on:change={event => file = event.currentTarget.files[0] || null} /></label>
          <p>PDF iki 20 MiB. Dokumentas išsaugomas kaip juodraštis; mokiniai jo nematys iki priskyrimo.</p>
          <button class="primary" disabled={!title.trim() || !file}>{busy ? 'Išsaugoma…' : 'Išsaugoti ir tęsti'}</button>
        </fieldset>
      </form>
      <button disabled={busy} on:click={back}>Grįžti į sąrašą</button>
    {:else if stage === 'students'}
      <h3>2. Mokiniai</h3>
      <div class="document-summary">
        <div><span class="section-eyebrow">Pasirinktas dokumentas</span><strong>{active.title}</strong><span class="version-badge">Versija {active.version_number}</span></div>
        <button disabled={!!previewId} on:click={() => preview(active.public_id)}>{previewId ? 'Atidaromas dokumentas…' : 'Peržiūrėti PDF'}</button>
      </div>
      {#if preloading}<p role="status">Įkeliami jau priskirti naudotojai…</p>{/if}
      {#if !preloading && !selectionReady}<button on:click={() => chooseStudents(active)}>Bandyti įkelti priskyrimus dar kartą</button>{/if}
      <fieldset disabled={busy || !selectionReady || selectingAll}>
        <section class="selection-panel" aria-label="Mokinių pasirinkimas">
          <div class="panel-heading"><span class="section-number" aria-hidden="true">1</span><div><h4>Pasirinkite mokinius</h4><p>Ieškokite ir pažymėkite reikiamas paskyras.</p></div></div>
          <div class="recipient-filters" role="group" aria-label="Naudotojų filtrai">
            {#each recipientFilters as [value, label]}<button type="button" class:active={recipientFilter === value} aria-pressed={recipientFilter === value} on:click={() => setRecipientFilter(value)}>{label}</button>{/each}
            <button type="button" class:active={recipientFilter === 'active_students_by_date'} aria-pressed={recipientFilter === 'active_students_by_date'} on:click={() => setRecipientFilter('active_students_by_date')}>Aktyvūs mokiniai pagal datą</button>
          </div>
          {#if recipientFilter === 'active_students_by_date'}
            <div class="toolbar">
              <label>Nuo<input type="date" bind:value={startDate} on:input={invalidateDateRange} /></label>
              <label>Iki<input type="date" bind:value={endDate} on:input={invalidateDateRange} /></label>
              <button type="button" on:click={applyDateRange}>Taikyti laikotarpį</button>
            </div>
            <p class="hint">Mokiniai, turintys bent vieną neatšauktą pamoką pasirinktu laikotarpiu, įskaitant abi datas (Vilniaus laiku). Atšaukti mokinių dalyvavimai neįtraukiami. Pakeitus datas, nauji pasirinkimai išvalomi; esami priskyrimai nesikeičia.</p>
            {#if appliedDates}<p role="status">Taikomas laikotarpis: {appliedDates.start_date} – {appliedDates.end_date}.</p>{/if}
          {/if}
          <label>Ieškoti mokinių<input bind:value={query} on:input={scheduleSearch} placeholder="Vardas, pavardė, naudotojo vardas arba el. paštas" maxlength="240" /></label>
          <p class="hint">Pasirinkite filtrą arba ieškokite įvedę bent 2 simbolius. Jau priskirtos paskyros nerodomos.{#if recipientFilter === 'active_students'} Aktyvūs mokiniai turi bent vieną neatšauktą pamoką nuo 2026-09-01.{/if}</p>
          <div class="toolbar"><button type="button" disabled={!dateRangeReady || (!!query.trim() && query.trim().length < 2)} on:click={selectAllFiltered}>{selectingAll ? 'Pasirenkami naudotojai…' : 'Pasirinkti visus pagal filtrą'}</button></div>
          <p class="hint">Pasirenkami tinkami naudotojai iš visų šio filtro ir paieškos puslapių. Iki 1000 naujų priskyrimų vienu kartu.</p>
          <div class="candidate-list" aria-busy={searching}>
            {#if searching}<p role="status" class="empty-selection">Ieškoma…</p>{/if}
            {#each candidates as student (student.id)}
              <label class="candidate-row" class:checked={selected.some(row => row.id === student.id)}>
                <span class="checkbox-cell"><input type="checkbox" aria-label={`Pasirinkti ${student.username}`}
                  checked={selected.some(row => row.id === student.id)}
                  disabled={student.can_assign === false || (!selected.some(row => row.id === student.id) && newSelected.length >= 1000)}
                  on:change={() => toggleStudent(student)} /></span>
                <span class="student-info"><strong>{student.first_name} {student.last_name}</strong><span class="student-meta">{student.username}</span>{#if student.email}<span class="student-meta">{student.email}</span>{/if}{#if student.can_assign === false}<span class="student-meta">Šiai paskyrai klientų dokumentų priskirti negalima.</span>{/if}</span>
              </label>
            {/each}
            {#if !searching && !candidates.length}<p class="empty-selection">{query.trim().length >= 2 ? 'Mokinių nerasta.' : 'Paieškos rezultatai bus rodomi čia.'}</p>{/if}
          </div>
          {#if candidates.length}<div class="toolbar picker-pagination"><button disabled={searchPage <= 1 || searching} on:click={() => search(searchPage - 1)}>Ankstesni mokiniai</button><span>Puslapis {searchPage}</span><button disabled={!searchNext || searching} on:click={() => search(searchPage + 1)}>Kiti mokiniai</button></div>{/if}
        </section>
        <section class="selection-panel selected-panel" aria-label="Pasirinkti mokiniai">
          <div class="panel-heading"><span class="section-number" aria-hidden="true">2</span><div><h4>Pasirinkti mokiniai</h4><p>Patikrinkite sąrašą prieš priskirdami dokumentą.</p></div><span class="selected-count">Pasirinkta: {selected.length}</span></div>
          <div class="toolbar"><button type="button" disabled={!selected.length} on:click={clearSelection}>Atžymėti visus</button><span class="hint">Naujų priskyrimų: {newSelected.length}</span></div>
          <p class="hint">Jau priskirti naudotojai pažymėti žemiau. Atžymėjimas nekeičia esamų priskyrimų — juos šalinkite skiltyje „Priskyrimai ir būsena“.</p>
          {#if selected.length}
            <ul class="selected-list">{#each selected as student (student.id)}<li>
              <span class="student-info"><strong>{student.first_name} {student.last_name}</strong><span class="student-meta">{student.username}</span>{#if existingIds.has(student.id)}<span class="assignment-status">Jau priskirta · {agreementStatusLabel(student.assignmentStatus)}</span>{/if}</span>
              <button class="remove-student" aria-label={`Pašalinti ${student.username}`} on:click={() => toggleStudent(student)}>Pašalinti</button>
            </li>{/each}</ul>
          {:else}<p class="empty-selection">Dar nepasirinkote mokinių.</p>{/if}
        </section>
        <section class="selection-panel confirmation-panel" aria-label="Priskyrimo patvirtinimas">
          <div class="panel-heading"><span class="section-number" aria-hidden="true">3</span><div><h4>Patvirtinkite priskyrimą</h4><p>Dokumentą gaus tik aukščiau pasirinkti mokiniai.</p></div></div>
          <label class="confirm"><input type="checkbox" bind:checked={confirmed} /><span>Patvirtinu, kad pasirinkti mokiniai turi pasirašyti „{active.title}“, versiją {active.version_number}.</span></label>
          <p class="hint">Priskyrimas parodys dokumentą mokinio paskyroje. Pasirašymas jo vardu nebus pradėtas.</p>
          <button class="primary" disabled={!confirmed || !newSelected.length} on:click={assign}>{busy ? 'Priskiriama…' : 'Priskirti pasirašyti'}</button>
        </section>
      </fieldset>
      <button disabled={busy} on:click={back}>Grįžti į sąrašą</button>
    {/if}
  {/if}
</section>

<style>
  .agreements { width: 100%; min-width: 0; color: #243833; text-align: left; }
  h2 { color: #14695b; margin-top: 0; }
  h2:focus { outline: none; }
  h3, h4 { margin: .7rem 0; }
  .toolbar { display: flex; flex-wrap: wrap; gap: .6rem; align-items: center; margin: .8rem 0; }
  article { padding: 1rem; border: 1px solid #d8e2de; border-radius: 12px; margin: 1rem 0; background: #fff; overflow-wrap: anywhere; }
  fieldset { border: 0; padding: 0; margin: 1rem 0; min-width: 0; }
  label { display: flex; flex-direction: column; gap: .4rem; margin: .8rem 0; flex: 1; }
  input { box-sizing: border-box; width: 100%; min-width: 0; padding: .7rem; border: 1px solid #8baca3; border-radius: 6px; font: inherit; }
  .confirm { flex-direction: row; align-items: flex-start; }
  .confirm input { width: auto; flex-shrink: 0; margin-top: .3rem; }
  button { padding: .65rem .85rem; border: 1px solid #8baca3; border-radius: 7px; background: #fff; color: #14695b; cursor: pointer; font: inherit; }
  button.primary { background: #14695b; color: white; border-color: #14695b; }
  button:disabled { opacity: .5; cursor: not-allowed; }
  button:focus-visible, input:focus-visible { outline: 3px solid #e4af3a; outline-offset: 2px; }
  li { margin: .6rem 0; overflow-wrap: anywhere; }
  ul { padding-left: 1.2rem; }
  .error { color: #912626; background: #fff0ef; padding: .8rem; border-radius: 6px; }
  .success { background: #e6f5ed; padding: .8rem; border-radius: 6px; }
  .details.selection-panel { border: 1px solid #cbdcd4; border-left: 4px solid #14695b; margin: 1.3rem 0 0; padding: 1.1rem; background: #f3f8f5; }
  .assignment-toolbar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .75rem; margin-bottom: 1rem; }
  .assignment-groups { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; margin: 1rem 0; align-items: start; }
  .assignment-group { min-width: 0; border: 1px solid #d8e2de; border-radius: 10px; overflow: hidden; background: #fff; }
  .assignment-group h5 { display: flex; align-items: center; justify-content: space-between; gap: .75rem; margin: 0; padding: .85rem .9rem; background: #edf2ef; color: #3e574e; font-size: .9rem; line-height: 1.5; }
  .assignment-group.waiting h5 { background: #fff4d9; color: #765923; }
  .assignment-group.complete h5 { background: #e1f2e7; color: #14695b; }
  .assignment-group.review h5 { background: #fff0ed; color: #912626; }
  .status-count { display: inline-flex; justify-content: center; align-items: center; min-width: 1.6rem; padding: .15rem .4rem; border-radius: 6px; background: #ffffffb3; font-size: .8rem; font-variant-numeric: tabular-nums; }
  .assignment-list { list-style: none; padding: 0; margin: 0; max-height: 380px; overflow-y: auto; background: white; }
  .assignment-list li { display: flex; flex-wrap: wrap; align-items: center; gap: .8rem; padding: .9rem; margin: 0; border-bottom: 1px solid #e2eae5; }
  .assignment-list li:last-child { border-bottom: 0; }
  @media (max-width: 800px) { .assignment-groups { grid-template-columns: minmax(0, 1fr); } }
  .assignment-status { align-self: flex-start; margin-top: .4rem; padding: .25rem .55rem; border-radius: 12px; background: #f2f0e8; font-size: .8rem; }
  .recipient-filters { display: flex; flex-wrap: wrap; gap: .5rem; padding: .8rem; border: 1px solid #d8e2de; border-radius: 8px; background: #f3f8f5; }
  .recipient-filters button { font-size: .85rem; }
  .recipient-filters button.active { color: white; background: #14695b; border-color: #14695b; }
  .document-summary { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 1rem; background: #f1f6f4; border: 1px solid #d8e2de; border-radius: 10px; padding: 1rem; margin: 1rem 0 1.5rem; }
  .document-summary > div { min-width: 0; flex: 1; overflow-wrap: anywhere; }
  .section-eyebrow { display: block; font-size: .75rem; color: #58746b; letter-spacing: .04em; margin-bottom: .4rem; }
  .version-badge { display: inline-block; margin-left: .6rem; font-size: .8rem; padding: .2rem .5rem; border-radius: 20px; background: white; color: #47685c; }
  .selection-panel { border: 1px solid #d8e2de; border-radius: 12px; padding: 1.1rem; margin-bottom: 1.25rem; background: white; min-width: 0; }
  .panel-heading { display: flex; flex-wrap: wrap; gap: .7rem; align-items: flex-start; margin-bottom: 1rem; }
  .panel-heading > div { flex: 1; min-width: 150px; }
  .panel-heading h4 { margin: 0 0 .3rem; }
  .panel-heading p, .hint { font-size: .85rem; color: #526b62; line-height: 1.55; margin: 0; }
  .section-number { flex-shrink: 0; display: grid; place-items: center; width: 1.8rem; height: 1.8rem; border-radius: 50%; background: #e6f0eb; color: #14695b; font-weight: 600; font-size: .85rem; }
  .candidate-list { max-height: 360px; overflow-y: auto; border: 1px solid #cbdcd4; border-radius: 8px; margin-top: 1rem; background: #fff; }
  .candidate-row { display: flex; flex-direction: row; gap: 0; align-items: stretch; margin: 0; border-bottom: 1px solid #e2eae5; cursor: pointer; }
  .candidate-row:last-child { border-bottom: 0; }
  .candidate-row:hover { background: #f3f8f5; }
  .candidate-row.checked { background: #eaf5ef; }
  .checkbox-cell { display: flex; align-items: center; justify-content: center; flex: 0 0 48px; border-right: 1px solid #e0e9e3; background: #f7faf8; }
  .checkbox-cell input, .confirm input { width: 18px; height: 18px; padding: 0; accent-color: #14695b; }
  .student-info { display: flex; flex-direction: column; gap: .15rem; flex: 1; min-width: 0; overflow-wrap: anywhere; }
  .candidate-row .student-info { padding: .8rem; }
  .student-meta { color: #587168; font-size: .85rem; }
  .empty-selection { padding: 1.2rem .7rem; color: #687b72; text-align: center; font-size: .85rem; margin: 0; }
  .picker-pagination { justify-content: space-between; font-size: .8rem; margin-bottom: 0; }
  .selected-panel { background: #f6faf8; }
  .selected-count { background: #e1eee7; color: #14695b; padding: .35rem .6rem; border-radius: 20px; font-size: .8rem; white-space: nowrap; }
  .selected-list { list-style: none; padding: 0; margin: 0; max-height: 360px; overflow-y: auto; }
  .selected-list li { display: flex; align-items: center; gap: .75rem; margin: .5rem 0 0; padding: .7rem .8rem; background: #fff; border: 1px solid #dce8e1; border-radius: 8px; }
  .remove-student { font-size: .8rem; padding: .4rem .6rem; }
  .confirmation-panel { background: #fffaf0; border-color: #e9d9b5; }
  .confirmation-panel .section-number { background: #f8e9ca; color: #7a5b23; }
  .confirmation-panel .confirm { background: white; border: 1px solid #e7ddc9; border-radius: 8px; padding: .8rem; gap: .75rem; line-height: 1.55; }
  .confirmation-panel .primary { margin-top: 1rem; }
  @media (max-width: 600px) { .toolbar > button { flex: 1 1 auto; } article { padding: .75rem; } }
</style>
